import React, { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown, Save, X, Check, Search, Filter, Download, Building2, CreditCard, BanknoteIcon, Clock, UserRound, FlipVertical as DragVertical, AlertCircle, Info } from 'lucide-react';

interface Bank {
  id: string;
  name: string;
  logo: string;
  displayOrder?: number;
  pageOrders?: {
    page: string;
    displayOrder: number;
  }[];
}

interface PageConfig {
  id: string;
  name: string;
  icon: React.ReactNode;
}

const banks: Bank[] = [
  {
    id: 'akbank',
    name: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
    displayOrder: 1,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 1 },
      { page: 'konut-kredisi', displayOrder: 2 },
      { page: 'tasit-kredisi', displayOrder: 1 },
      { page: 'kobi-kredisi', displayOrder: 3 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 1 },
      { page: 'kredi-kartlari', displayOrder: 2 },
      { page: 'mevduat', displayOrder: 1 }
    ]
  },
  {
    id: 'garantibbva',
    name: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
    displayOrder: 2,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 2 },
      { page: 'konut-kredisi', displayOrder: 1 },
      { page: 'tasit-kredisi', displayOrder: 2 },
      { page: 'kobi-kredisi', displayOrder: 1 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 2 },
      { page: 'kredi-kartlari', displayOrder: 1 },
      { page: 'mevduat', displayOrder: 3 }
    ]
  },
  {
    id: 'yapikredi',
    name: 'Yapı Kredi',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png',
    displayOrder: 3,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 3 },
      { page: 'konut-kredisi', displayOrder: 3 },
      { page: 'tasit-kredisi', displayOrder: 3 },
      { page: 'kobi-kredisi', displayOrder: 2 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 3 },
      { page: 'kredi-kartlari', displayOrder: 3 },
      { page: 'mevduat', displayOrder: 2 }
    ]
  },
  {
    id: 'qnb',
    name: 'QNB',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png',
    displayOrder: 4,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 4 },
      { page: 'konut-kredisi', displayOrder: 4 },
      { page: 'tasit-kredisi', displayOrder: 4 },
      { page: 'kobi-kredisi', displayOrder: 4 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 4 },
      { page: 'kredi-kartlari', displayOrder: 4 },
      { page: 'mevduat', displayOrder: 4 }
    ]
  },
  {
    id: 'isbankasi',
    name: 'İş Bankası',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png',
    displayOrder: 5,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 5 },
      { page: 'konut-kredisi', displayOrder: 5 },
      { page: 'tasit-kredisi', displayOrder: 5 },
      { page: 'kobi-kredisi', displayOrder: 5 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 5 },
      { page: 'kredi-kartlari', displayOrder: 5 },
      { page: 'mevduat', displayOrder: 5 }
    ]
  },
  {
    id: 'enpara',
    name: 'En Para',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/cb4abd40-216e-404a-9e2b-4dd7eda7462e.png',
    displayOrder: 6,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 6 },
      { page: 'konut-kredisi', displayOrder: 6 },
      { page: 'tasit-kredisi', displayOrder: 6 },
      { page: 'kobi-kredisi', displayOrder: 6 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 6 },
      { page: 'kredi-kartlari', displayOrder: 6 },
      { page: 'mevduat', displayOrder: 6 }
    ]
  }
];

const pages: PageConfig[] = [
  { id: 'ihtiyac-kredisi', name: 'İhtiyaç Kredisi', icon: <BanknoteIcon size={18} className="text-green-600" /> },
  { id: 'konut-kredisi', name: 'Konut Kredisi', icon: <BanknoteIcon size={18} className="text-blue-600" /> },
  { id: 'tasit-kredisi', name: 'Taşıt Kredisi', icon: <BanknoteIcon size={18} className="text-purple-600" /> },
  { id: 'kobi-kredisi', name: 'Kobi Kredisi', icon: <BanknoteIcon size={18} className="text-orange-600" /> },
  { id: 'kredi-kartlari', name: 'Kredi Kartları', icon: <CreditCard size={18} className="text-red-600" /> },
  { id: 'mevduat', name: 'Mevduat', icon: <Clock size={18} className="text-teal-600" /> },
  { id: 'uzaktan-musteri-edinimi', name: 'Uzaktan Müşteri Edinimi', icon: <UserRound size={18} className="text-indigo-600" /> }
];

const OrderManagement = () => {
  const [banksList, setBanksList] = useState<Bank[]>([]);
  const [selectedPage, setSelectedPage] = useState<string>(pages[0].id);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize banks list
  useEffect(() => {
    setBanksList([...banks]);
  }, []);

  // Filter banks based on search term
  const filteredBanks = banksList.filter(bank => 
    bank.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort banks by their display order for the selected page
  const sortedBanks = [...filteredBanks].sort((a, b) => {
    const aOrder = a.pageOrders?.find(po => po.page === selectedPage)?.displayOrder || a.displayOrder || 999;
    const bOrder = b.pageOrders?.find(po => po.page === selectedPage)?.displayOrder || b.displayOrder || 999;
    return aOrder - bOrder;
  });

  // Move bank up in order
  const moveBankUp = (bankId: string) => {
    const bankIndex = sortedBanks.findIndex(bank => bank.id === bankId);
    if (bankIndex <= 0) return; // Already at the top
    
    const updatedBanks = [...banksList];
    
    // Find the banks we're swapping
    const currentBank = updatedBanks.find(bank => bank.id === bankId);
    const previousBank = sortedBanks[bankIndex - 1];
    
    if (!currentBank || !previousBank) return;
    
    // Get current orders
    const currentBankOrder = currentBank.pageOrders?.find(po => po.page === selectedPage)?.displayOrder || currentBank.displayOrder || 999;
    const previousBankOrder = previousBank.pageOrders?.find(po => po.page === selectedPage)?.displayOrder || previousBank.displayOrder || 999;
    
    // Update orders for the selected page
    updatedBanks.forEach(bank => {
      if (bank.id === bankId) {
        // Update current bank's order for this page
        if (!bank.pageOrders) {
          bank.pageOrders = [];
        }
        
        const pageOrderIndex = bank.pageOrders.findIndex(po => po.page === selectedPage);
        if (pageOrderIndex >= 0) {
          bank.pageOrders[pageOrderIndex].displayOrder = previousBankOrder;
        } else {
          bank.pageOrders.push({ page: selectedPage, displayOrder: previousBankOrder });
        }
      } else if (bank.id === previousBank.id) {
        // Update previous bank's order for this page
        if (!bank.pageOrders) {
          bank.pageOrders = [];
        }
        
        const pageOrderIndex = bank.pageOrders.findIndex(po => po.page === selectedPage);
        if (pageOrderIndex >= 0) {
          bank.pageOrders[pageOrderIndex].displayOrder = currentBankOrder;
        } else {
          bank.pageOrders.push({ page: selectedPage, displayOrder: currentBankOrder });
        }
      }
    });
    
    setBanksList(updatedBanks);
    setHasChanges(true);
  };

  // Move bank down in order
  const moveBankDown = (bankId: string) => {
    const bankIndex = sortedBanks.findIndex(bank => bank.id === bankId);
    if (bankIndex >= sortedBanks.length - 1) return; // Already at the bottom
    
    const updatedBanks = [...banksList];
    
    // Find the banks we're swapping
    const currentBank = updatedBanks.find(bank => bank.id === bankId);
    const nextBank = sortedBanks[bankIndex + 1];
    
    if (!currentBank || !nextBank) return;
    
    // Get current orders
    const currentBankOrder = currentBank.pageOrders?.find(po => po.page === selectedPage)?.displayOrder || currentBank.displayOrder || 999;
    const nextBankOrder = nextBank.pageOrders?.find(po => po.page === selectedPage)?.displayOrder || nextBank.displayOrder || 999;
    
    // Update orders for the selected page
    updatedBanks.forEach(bank => {
      if (bank.id === bankId) {
        // Update current bank's order for this page
        if (!bank.pageOrders) {
          bank.pageOrders = [];
        }
        
        const pageOrderIndex = bank.pageOrders.findIndex(po => po.page === selectedPage);
        if (pageOrderIndex >= 0) {
          bank.pageOrders[pageOrderIndex].displayOrder = nextBankOrder;
        } else {
          bank.pageOrders.push({ page: selectedPage, displayOrder: nextBankOrder });
        }
      } else if (bank.id === nextBank.id) {
        // Update next bank's order for this page
        if (!bank.pageOrders) {
          bank.pageOrders = [];
        }
        
        const pageOrderIndex = bank.pageOrders.findIndex(po => po.page === selectedPage);
        if (pageOrderIndex >= 0) {
          bank.pageOrders[pageOrderIndex].displayOrder = currentBankOrder;
        } else {
          bank.pageOrders.push({ page: selectedPage, displayOrder: currentBankOrder });
        }
      }
    });
    
    setBanksList(updatedBanks);
    setHasChanges(true);
  };

  // Save changes
  const saveChanges = () => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      // In a real app, this would be an API call to save the changes
      console.log('Saving bank order changes:', banksList);
      
      setIsSaving(false);
      setHasChanges(false);
      setSaveSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }, 1000);
  };

  // Reset changes
  const resetChanges = () => {
    setBanksList([...banks]);
    setHasChanges(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sıralama Yönetimi</h1>
              <p className="text-slate-600">Sayfa bazlı banka sıralamalarını yönetin</p>
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <>
                  <button 
                    onClick={resetChanges}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    <X size={20} />
                    <span>Değişiklikleri İptal Et</span>
                  </button>
                  <button 
                    onClick={saveChanges}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                        <span>Kaydediliyor...</span>
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        <span>Değişiklikleri Kaydet</span>
                      </>
                    )}
                  </button>
                </>
              )}
              {saveSuccess && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg">
                  <Check size={20} />
                  <span>Başarıyla kaydedildi!</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Page Selection and Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Sayfa Seçin
              </label>
              <select
                value={selectedPage}
                onChange={(e) => setSelectedPage(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              >
                {pages.map(page => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Banka Ara
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Banka adı ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">Sıralama Nasıl Çalışır?</h3>
              <p className="text-sm text-blue-700">
                Bankaların sıralamasını değiştirmek için yukarı/aşağı oklarını kullanın. Değişiklikler, seçili sayfa için geçerli olacaktır. 
                Değişiklikleri kaydetmek için "Değişiklikleri Kaydet" butonuna tıklayın.
              </p>
            </div>
          </div>
        </div>

        {/* Banks List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-[#F7BA45]" />
              <h2 className="font-medium text-slate-800">
                {pages.find(p => p.id === selectedPage)?.name} Sayfası Banka Sıralaması
              </h2>
            </div>
          </div>
          
          <div className="divide-y divide-slate-200">
            {sortedBanks.map((bank, index) => {
              const bankOrder = bank.pageOrders?.find(po => po.page === selectedPage)?.displayOrder || bank.displayOrder || 999;
              
              return (
                <div key={bank.id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-slate-400 font-medium w-8 text-center">
                        {index + 1}
                      </div>
                      <div className="flex items-center gap-3">
                        <DragVertical size={20} className="text-slate-300 cursor-move" />
                        <img 
                          src={bank.logo} 
                          alt={bank.name}
                          className="h-8 w-8 object-contain"
                        />
                        <div>
                          <div className="font-medium text-slate-800">{bank.name}</div>
                          <div className="text-xs text-slate-500">
                            Sıra: {bankOrder}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => moveBankUp(bank.id)}
                        disabled={index === 0}
                        className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowUp size={18} />
                      </button>
                      <button
                        onClick={() => moveBankDown(bank.id)}
                        disabled={index === sortedBanks.length - 1}
                        className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowDown size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {sortedBanks.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                <Building2 size={40} className="mx-auto mb-2 text-slate-300" />
                <p>Hiç banka bulunamadı</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;